import Toast from 'react-native-toast-message'
import { getCurrentOrgID } from './organization';
import ErrorHandler from '../utils/ErrorHandler';
import { supabase } from './client';
import useAuthStore from '@/data/store/useAuthStore';

// ─── Templates ───

const getEmailTemplates = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('EmailTemplates')
        .select('*')
        .eq('organizationID', orgID)
        .eq('isArchived', false)
        .order('updatedAt', { ascending: false });
    
    if (error) {
        ErrorHandler(Error(error.message), { query: "getEmailTemplates", error }, `Something went wrong loading email templates`);
        return [];
    }
    return data;
};

const getEmailTemplate = async (id) => {
    const { data, error } = await supabase
        .from('EmailTemplates')
        .select('*')
        .eq('id', id)
        .single();
    
    if (error) {
        ErrorHandler(Error(error.message), { query: "getEmailTemplate", id, error }, `Something went wrong loading the email template`);
        return null;
    }
    return data;
};

const saveEmailTemplate = async ({ details }) => {
    details.organizationID = await getCurrentOrgID();
    details.updatedAt = new Date().toISOString();
    
    const isEdit = !!details.id;
    let data, error;

    if (isEdit) {
        const { id, organizationID, ...updateFields } = details;
        ({ data, error } = await supabase.from("EmailTemplates").update(updateFields).eq("id", id).eq("organizationID", organizationID).select().maybeSingle());
    } else {
        const user = useAuthStore.getState().user;
        details.createdByID = user?.personID || null;
        ({ data, error } = await supabase.from("EmailTemplates").insert(details).select().maybeSingle());
    }

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "saveEmailTemplate", input: details, ...error }, `Something went wrong ${isEdit ? "updating" : "creating"} the email template`);
        throw Error();
    }

    Toast.show({
        type: 'success',
        text2: `Template has been successfully ${isEdit ? "updated" : "created"}`
    });

    return data;
};

const deleteEmailTemplate = async (id) => {
    const orgID = await getCurrentOrgID();
    const { error } = await supabase
        .from('EmailTemplates')
        .update({ isArchived: true })
        .eq('id', id)
        .eq('organizationID', orgID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "deleteEmailTemplate", id, error }, `Something went wrong deleting the email template`);
        throw Error();
    }

    Toast.show({
        type: 'success',
        text2: 'Template has been deleted'
    });
};

// ─── Campaigns ───

const getEmailCampaigns = async () => {
    const orgID = await getCurrentOrgID();
    const { data, error } = await supabase
        .from('EmailCampaigns')
        .select('*, Sender:People!sentByID(id, firstName, lastName), Template:EmailTemplates!templateID(id, name)')
        .eq('organizationID', orgID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getEmailCampaigns", error }, `Something went wrong loading email campaigns`);
        return [];
    }
    return data;
};

const getEmailCampaignRecipients = async (campaignID) => {
    const orgID = await getCurrentOrgID();
    const recipients = [];
    let offset = 0;
    while (true) {
        const { data, error } = await supabase
            .from('EmailCampaignRecipients')
            .select('id, name, email, status, notes, createdAt, personID, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey)')
            .eq('organizationID', orgID)
            .eq('campaignID', campaignID)
            .order('createdAt', { ascending: true })
            .order('id', { ascending: true })
            .range(offset, offset + 499);

        if (error) {
            ErrorHandler(Error(error.message), { query: 'getEmailCampaignRecipients', error }, 'Something went wrong loading email recipients');
            throw error;
        }
        recipients.push(...data);
        if (data.length < 500) return recipients;
        offset += 500;
    }
};

const getEmailDeliveryStatuses = async (campaignID) => {
    const { data, error } = await supabase.functions.invoke('send-email', {
        body: { action: 'delivery-status', campaignID },
    });
    if (error || data?.error) throw error || Error(data.error);
    return { statuses: data?.statuses || {}, reasons: data?.reasons || {} };
};

// ─── Sending ───

const sendTestEmail = async ({ subject, templateID }) => {
    const { data, error } = await supabase.functions.invoke('send-email', {
        body: { action: 'send-test', subject, templateID }
    });

    if (error || data?.error) {
        let detail = data?.error || error?.message;
        try {
            if (error?.context && typeof error.context.json === 'function') {
                detail = (await error.context.json())?.error || detail;
            }
        } catch { /* response body not JSON */ }
        Toast.show({ type: 'error', text2: detail || 'Test email could not be sent' });
        throw error || Error(detail);
    }

    Toast.show({ type: 'success', text2: `Test email sent to ${data.email}` });
    return data;
};

const sendEmail = async ({ type, subject, recipientIDs, body, templateID, scheduledFor }) => {
    const { data, error } = await supabase.functions.invoke('send-email', {
        body: { type, subject, recipientIDs, body, templateID, scheduledFor }
    });

    if (error) {
        // FunctionsHttpError carries the original Response on error.context — read
        // the body for the real server-side reason instead of the generic
        // "Edge Function returned a non-2xx status code". See CHURCH-APP-16W.
        let detail = error.message;
        try {
            if (error.context && typeof error.context.json === 'function') {
                const body = await error.context.json();
                detail = body?.error || detail;
            }
        } catch { /* response body not JSON */ }

        // 4xx are expected user-facing errors (no valid recipients, all unsubscribed,
        // auth failures, quota limits) — don't report to Sentry, just show a toast.
        const status = error.context?.status;
        if (status >= 400 && status < 500) {
            Toast.show({ type: 'error', text2: detail });
            throw error;
        }
        ErrorHandler(Error(detail), { mutation: "sendEmail", type, error, serverError: detail }, detail !== error.message ? detail : `Something went wrong sending the email`);
        throw error;
    }

    if (data?.error) {
        Toast.show({ type: 'error', text2: data.error });
        throw Error(data.error);
    }

    const sentCount = data?.sentCount || 0;
    const skippedCount = data?.skippedCount || 0;
    const unsubscribedCount = data?.unsubscribedCount || 0;
    const failedCount = data?.failedCount || 0;

    const scheduledText = data?.scheduledFor
        ? new Date(data.scheduledFor).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        : null;
    let message = scheduledText
        ? `Email scheduled for ${scheduledText}`
        : `Email accepted for ${sentCount} recipient(s). Delivery is still processing`;
    if (skippedCount > 0) message += ` (${skippedCount} skipped - no valid email)`;
    if (unsubscribedCount > 0) message += ` (${unsubscribedCount} unsubscribed)`;
    if (failedCount > 0) message += ` (${failedCount} failed)`;

    Toast.show({ type: 'success', text2: message });
    return data;
};

export { getEmailTemplates, getEmailTemplate, saveEmailTemplate, deleteEmailTemplate, getEmailCampaigns, getEmailCampaignRecipients, getEmailDeliveryStatuses, sendTestEmail, sendEmail };
